import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiUsers, FiHeart, FiTrendingUp, FiFileText, FiTarget } from 'react-icons/fi';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];

const StatCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <div style={{ background: '#fff', borderRadius: 16, padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -10, right: -10, width: 80, height: 80, borderRadius: '50%', background: `${color}10` }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 13, color: '#64748b', fontWeight: 500, marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
        {sub && <p style={{ fontSize: 12, color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#64748b', marginTop: 4, fontWeight: 500 }}>{sub}</p>}
      </div>
      <div style={{ width: 48, height: 48, borderRadius: 12, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon size={22} color={color} />
      </div>
    </div>
  </div>
);

const ChartCard = ({ title, children, style: s }) => (
  <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', ...s }}>
    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 20 }}>{title}</h3>
    {children}
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [overview, ai] = await Promise.all([
          dashboardAPI.getOverview(),
          dashboardAPI.getAIInsights().catch(() => ({ data: { insights: null } }))
        ]);
        setData(overview.data);
        setInsights(ai.data.insights);
      // Safeguard: if insights came as a string, try to parse it
      if (typeof ai.data.insights === 'string') {
        try { const parsed = JSON.parse(ai.data.insights.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '')); setInsights(parsed); } catch {}
      }
      } catch (err) {
        console.error('Dashboard load error:', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div style={{ width: 40, height: 40, border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;

  const stats = data?.stats || {};
  const charts = data?.charts || {};

  const trendData = (charts.diseaseTrends || []).reduce((acc, item) => {
    const key = `${item._id.year}-${String(item._id.month).padStart(2, '0')}`;
    if (!acc[key]) acc[key] = { month: key };
    acc[key][item._id.disease] = (acc[key][item._id.disease] || 0) + item.count;
    acc[key].total = (acc[key].total || 0) + item.count;
    return acc;
  }, {});
  const trendArr = Object.values(trendData).sort((a, b) => a.month.localeCompare(b.month));

  const severityData = (charts.casesBySeverity || []).map(s => ({ name: s._id || 'Unknown', value: s.count }));
  const regionData = (charts.casesByRegion || []).filter(r => r._id).map(r => ({ name: r._id, total: r.count, active: r.active }));
  const ageData = (charts.ageDistribution || []).filter(a => a._id).map(a => ({ name: a._id, count: a.count }));
  const diseaseData = (charts.diseaseByType || []).filter(d => d._id).map(d => ({ name: d._id, count: d.count }));

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>EpiSurveillance Dashboard</h1>
        <p style={{ color: '#64748b', marginTop: 4 }}>Welcome back, {user?.firstName}. Here's your health intelligence overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard icon={FiActivity} label="Total Disease Cases" value={stats.totalCases || 0} sub={`${stats.activeCases || 0} active`} color="#3b82f6" trend="up" />
        <StatCard icon={FiAlertTriangle} label="Active Cases" value={stats.activeCases || 0} sub="Requires attention" color="#ef4444" />
        <StatCard icon={FiCheckCircle} label="Resolved Cases" value={stats.resolvedCases || 0} sub="Successfully treated" color="#10b981" trend="up" />
        <StatCard icon={FiHeart} label="Total Births" value={stats.totalBirths || 0} color="#ec4899" />
        <StatCard icon={FiTrendingUp} label="Total Deaths" value={stats.totalDeaths || 0} color="#6b7280" />
        <StatCard icon={FiTarget} label="Active Programs" value={stats.activePrograms || 0} color="#8b5cf6" />
        <StatCard icon={FiFileText} label="Assessments" value={stats.totalAssessments || 0} color="#f59e0b" />
        <StatCard icon={FiUsers} label="Active Users" value={stats.totalUsers || 0} sub={`${stats.pendingUsers || 0} pending`} color="#06b6d4" />
      </div>

      {/* Charts Row 1 */}
      <div className="chart-grid-2-1" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Disease Trend (Monthly)">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={trendArr}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#3b82f620" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Cases by Severity">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={severityData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {severityData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Cases by Region">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={regionData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Total" />
              <Bar dataKey="active" fill="#ef4444" radius={[4, 4, 0, 0]} name="Active" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Diseases">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={diseaseData.slice(0, 8)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* AI Insights & Age */}
      <div className="chart-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <ChartCard title="Age Distribution">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="AI-Powered Insights">
          {insights?.aiUnavailable ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>AI insights are currently unavailable. Your data below is unaffected.</div>
          ) : insights ? (
            <div>
              {(insights.insights || []).map((insight, i) => (
                <div key={i} style={{ padding: '12px 16px', background: i % 2 === 0 ? '#f0f9ff' : '#f0fdf4', borderRadius: 10, marginBottom: 8, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                  <span style={{ color: '#3b82f6', fontWeight: 700, marginRight: 6 }}>💡</span>
                  {typeof insight === 'string' ? insight : JSON.stringify(insight)}
                </div>
              ))}
              {insights.summary && <div style={{ padding: 16, background: '#fefce8', borderRadius: 10, marginTop: 12, fontSize: 13, color: '#713f12', lineHeight: 1.6 }}><strong>Summary:</strong> {insights.summary}</div>}
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Configure OpenAI API key for AI insights</div>
          )}
        </ChartCard>
      </div>

      {/* Recent Outbreaks */}
      {data?.recentOutbreaks?.length > 0 && (
        <ChartCard title="Recent Outbreak Alerts">
          <div style={{ display: 'grid', gap: 10 }}>
            {data.recentOutbreaks.map(ob => (
              <div key={ob._id} className="outbreak-item" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', background: '#fef2f2', borderRadius: 10, borderLeft: '4px solid #ef4444' }}>
                <FiAlertTriangle color="#ef4444" size={20} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, color: '#991b1b' }}>{ob.disease}</span>
                  <span style={{ color: '#64748b', fontSize: 13, marginLeft: 12 }}>{ob.location?.region}</span>
                </div>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(ob.reportedDate).toLocaleDateString()}</span>
                <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ob.severity === 'critical' ? '#fecaca' : ob.severity === 'severe' ? '#fed7aa' : '#e2e8f0', color: ob.severity === 'critical' ? '#991b1b' : ob.severity === 'severe' ? '#9a3412' : '#475569' }}>{ob.severity}</span>
              </div>
            ))}
          </div>
        </ChartCard>
      )}
    </div>
  );
};

export default Dashboard;
